import { useLogoStore, type ContainerShape } from "../../state/logoStore";
import { CONTAINER_SHAPES, OBJECT_SHAPE_IDS } from "../../engine/containerShapes";
import { ColorRow, Slider } from "./EditorControls";
import { ContainerShapeThumb, PatternPicker } from "./Pickers";

export function ContainerPanel() {
  const s = useLogoStore();
  const BASIC_SHAPES: Array<{ id: ContainerShape; label: string }> = [
    { id: "none",    label: "None"    },
    { id: "circle",  label: "Circle"  },
    { id: "rounded", label: "Rounded" },
    { id: "square",  label: "Square"  },
  ];
  const objectShapes = OBJECT_SHAPE_IDS.map((id) => {
    const def = CONTAINER_SHAPES[id];
    return { id, label: def?.name ?? id };
  });
  return (
    <>
      <div className="section-label">Container</div>
      <div style={{ fontSize: 12, color: "#475569", marginBottom: 10, lineHeight: 1.5 }}>
        Wrap your icon in a colored shape — adds presence and helps it pop on busy backgrounds.
      </div>
      <div className="pattern-cat-heading">Basic</div>
      <div className="container-grid">
        {BASIC_SHAPES.map((sh) => (
          <button
            key={sh.id}
            className={`container-tile ${s.containerShape === sh.id ? "active" : ""}`}
            onClick={() => { s.pushHistory(); s.setStyle({ containerShape: sh.id }); }}
          >
            <span className="container-preview"><ContainerShapeThumb id={sh.id} /></span>
            <span>{sh.label}</span>
          </button>
        ))}
      </div>
      <div className="pattern-cat-heading">Objects</div>
      <div className="pattern-cat">
        <div className="container-grid">
          {objectShapes.map((sh) => (
            <button
              key={sh.id}
              className={`container-tile ${s.containerShape === sh.id ? "active" : ""}`}
              onClick={() => { s.pushHistory(); s.setStyle({ containerShape: sh.id }); }}
              title={sh.label}
            >
              <span className="container-preview"><ContainerShapeThumb id={sh.id} /></span>
              <span>{sh.label}</span>
            </button>
          ))}
        </div>
      </div>
      {s.containerShape !== "none" && (
        <>
          <ColorRow label="Container color" color={s.containerColor} onChange={(c) => { s.pushHistory(); s.setStyle({ containerColor: c }); }} />
          <Slider
            label="Color opacity"
            min={0} max={100} step={1}
            value={s.containerColorOpacity}
            onChange={(v) => s.setStyle({ containerColorOpacity: v })}
            onCommit={() => s.pushHistory()}
          />

          <div className="section-label" style={{ marginTop: 14 }}>Gradient</div>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 8, lineHeight: 1.5 }}>
            Blend the container color into a second color — none, linear sweep, or radial.
          </div>
          <div className="container-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
            {([
              { id: "none",   label: "Solid"  },
              { id: "linear", label: "Linear" },
              { id: "radial", label: "Radial" },
            ] as const).map((g) => (
              <button
                key={g.id}
                className={`container-tile ${s.containerGradient === g.id ? "active" : ""}`}
                onClick={() => { s.pushHistory(); s.setStyle({ containerGradient: g.id }); }}
                title={g.label}
              >
                <span className="container-preview">
                  <svg viewBox="0 0 24 24">
                    {g.id === "none"   && <rect x="3" y="3" width="18" height="18" rx="3" fill={s.containerColor} />}
                    {g.id === "linear" && (
                      <>
                        <defs>
                          <linearGradient id={`lg-prev-${g.id}`} x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%"  stopColor={s.containerColor} />
                            <stop offset="100%" stopColor={s.containerColor2} />
                          </linearGradient>
                        </defs>
                        <rect x="3" y="3" width="18" height="18" rx="3" fill={`url(#lg-prev-${g.id})`} />
                      </>
                    )}
                    {g.id === "radial" && (
                      <>
                        <defs>
                          <radialGradient id={`rg-prev-${g.id}`} cx="0.5" cy="0.5" r="0.55">
                            <stop offset="0%"  stopColor={s.containerColor} />
                            <stop offset="100%" stopColor={s.containerColor2} />
                          </radialGradient>
                        </defs>
                        <rect x="3" y="3" width="18" height="18" rx="3" fill={`url(#rg-prev-${g.id})`} />
                      </>
                    )}
                  </svg>
                </span>
                <span>{g.label}</span>
              </button>
            ))}
          </div>
          {s.containerGradient !== "none" && (
            <>
              <ColorRow
                label="Second color"
                color={s.containerColor2}
                onChange={(c) => { s.pushHistory(); s.setStyle({ containerColor2: c }); }}
              />
              {s.containerGradient === "linear" && (
                <Slider
                  label="Gradient angle"
                  min={0} max={360} step={1}
                  value={s.containerGradientAngle}
                  onChange={(v) => s.setStyle({ containerGradientAngle: v })}
                  onCommit={() => s.pushHistory()}
                />
              )}
            </>
          )}

          <div className="section-label" style={{ marginTop: 16 }}>Container pattern</div>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 10, lineHeight: 1.5 }}>
            Same catalog as the symbol — apply any pattern to the container shape.
          </div>
          <PatternPicker
            selected={s.containerPattern}
            onPick={(id) => { s.pushHistory(); s.setStyle({ containerPattern: id }); }}
          />
          {s.containerPattern !== "none" && (
            <>
              <ColorRow
                label="Pattern color"
                color={s.containerPatternColor}
                onChange={(c) => { s.pushHistory(); s.setStyle({ containerPatternColor: c }); }}
              />
              <Slider
                label="Pattern density"
                min={0.5} max={2} step={0.1}
                value={s.containerPatternDensity}
                onChange={(v) => s.setStyle({ containerPatternDensity: v })}
                onCommit={() => s.pushHistory()}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
